import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Grid, Group, Paper, SegmentedControl, Select, Stack, Text } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import SeatLayout from 'components/booking/SeatLayout';
import tableService from 'services/tableService';
import intraService from 'services/intraService';
import { BlockedLocation, LanBooking, LanRegistration } from 'types/intra';
import { TableGroup } from 'types/common';
import {
    bookedLocations,
    bookingParticipantLabel,
    isAssignedLocation,
    resolveSeatAssignIntent,
    toErrorMessage
} from './intraUtils';
import SeatSwapConfirmDialog from './SeatSwapConfirmDialog';
import 'assets/Table.scss';

type LocationMode = 'block' | 'assign';

interface LocationsSectionProps {
    accessToken: string | null;
    registrations: LanRegistration[];
    bookings: LanBooking[];
    isOperating: (key: string) => boolean;
    onAssign: (bookingId: string, location: string | null) => Promise<void>;
    onSwap: (bookingId: string, location: string) => Promise<void>;
}

const LocationsSection: React.FC<LocationsSectionProps> = ({
    accessToken,
    registrations,
    bookings,
    isOperating,
    onAssign,
    onSwap
}) => {
    const [mode, setMode] = useState<LocationMode>('block');
    const [tableGroups, setTableGroups] = useState<TableGroup[]>([]);
    const [blocked, setBlocked] = useState<BlockedLocation[]>([]);
    const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [pendingSwap, setPendingSwap] = useState<{ location: string; occupant: LanBooking } | null>(null);

    const loadMapData = useCallback(async () => {
        setLoading(true);
        try {
            const [groups, blockedLocations] = await Promise.all([
                tableService.getAllTableGroups(),
                intraService.fetchBlockedLocations(accessToken ?? undefined)
            ]);
            setTableGroups(groups);
            setBlocked(blockedLocations);
            setError(null);
        } catch (err) {
            setError(toErrorMessage(err));
        } finally {
            setLoading(false);
        }
    }, [accessToken]);

    useEffect(() => {
        loadMapData();
    }, [loadMapData]);

    const occupied = useMemo(() => bookedLocations(bookings), [bookings]);
    const blockedIds = useMemo(() => blocked.map(item => item.location), [blocked]);
    const selectedBooking = bookings.find(booking => booking.id === selectedBookingId) ?? null;

    const bookingOptions = useMemo(() => {
        return [...bookings]
            .sort((a, b) => Number(isAssignedLocation(a.location)) - Number(isAssignedLocation(b.location)))
            .map(booking => ({
                value: booking.id,
                label: bookingParticipantLabel(booking, registrations)
            }));
    }, [bookings, registrations]);

    const disabledLocations = mode === 'block' ? occupied : [];

    const handleSeatClick = async (location: string) => {
        if (mode === 'block') {
            if (occupied.includes(location) || isOperating(`block-${location}`)) {
                return;
            }
            try {
                if (blockedIds.includes(location)) {
                    await intraService.unblockLocation(location, accessToken ?? undefined);
                    setBlocked(prev => prev.filter(item => item.location !== location));
                } else {
                    const created = await intraService.blockLocation(location, accessToken ?? undefined);
                    setBlocked(prev => [...prev, created]);
                }
                setError(null);
            } catch (err) {
                setError(toErrorMessage(err));
            }
            return;
        }

        if (!selectedBooking || isOperating(`booking-location-${selectedBooking.id}`)) {
            return;
        }

        const intent = resolveSeatAssignIntent(selectedBooking, location, bookings);
        if (intent.type === 'swap') {
            setPendingSwap({ location: intent.location, occupant: intent.occupant });
            return;
        }

        try {
            await onAssign(selectedBooking.id, intent.type === 'clear' ? null : intent.location);
            setError(null);
        } catch (err) {
            setError(toErrorMessage(err));
        }
    };

    const handleConfirmSwap = async () => {
        if (!selectedBooking || !pendingSwap) {
            return;
        }
        try {
            await onSwap(selectedBooking.id, pendingSwap.location);
            setPendingSwap(null);
            setError(null);
        } catch (err) {
            setError(toErrorMessage(err));
        }
    };

    return (
        <Stack gap="md">
            <Group justify="space-between" align="flex-end">
                <SegmentedControl
                    value={mode}
                    onChange={value => setMode(value as LocationMode)}
                    data={[
                        { label: 'Blokkaa', value: 'block' },
                        { label: 'Sijoita', value: 'assign' }
                    ]}
                    color="violet"
                />
                {mode === 'assign' && (
                    <Select
                        label="Osallistuja"
                        placeholder="Valitse varaus"
                        data={bookingOptions}
                        value={selectedBookingId}
                        onChange={setSelectedBookingId}
                        searchable
                        clearable
                        style={{ minWidth: 280 }}
                    />
                )}
            </Group>
            <Text size="sm" c="dimmed">
                {mode === 'block'
                    ? 'Klikkaa vapaata paikkaa blokataksesi sen verkkovaraukselta. Blokatut paikat voi yhä sijoittaa osallistujalle Sijoita-tilassa.'
                    : 'Valitse ilmoittautunut ja klikkaa paikkaa. Varattu paikka avaa vahvistuksen paikanvaihdosta. Klikkaa nykyistä paikkaa poistaaksesi sijoituksen.'}
            </Text>
            {error && (
                <Alert color="red" icon={<IconInfoCircle size={16} />}>
                    {error}
                </Alert>
            )}
            <div className="table-clearfix">
                <Grid>
                    <Grid.Col span={2}>
                        <div className="table-legend table-free"></div>
                        <span>Vapaa</span>
                    </Grid.Col>
                    <Grid.Col span={2}>
                        <div className="table-legend table-disabled"></div>
                        <span>Blokkattu</span>
                    </Grid.Col>
                    <Grid.Col span={2}>
                        <div className="table-legend table-booking"></div>
                        <span>Valittu</span>
                    </Grid.Col>
                    <Grid.Col span={2}>
                        <div className="table-legend table-booked"></div>
                        <span>Varattu</span>
                    </Grid.Col>
                </Grid>
            </div>
            {loading ? (
                <Paper p="md" withBorder>
                    <Text>Ladataan pohjakarttaa...</Text>
                </Paper>
            ) : (
                <SeatLayout
                    tableGroups={tableGroups}
                    booked={occupied}
                    booking={selectedBooking?.location ?? ''}
                    toggle={handleSeatClick}
                    blockedLocations={blockedIds}
                    disabledLocations={disabledLocations}
                    allowSelectOccupied={mode === 'assign'}
                />
            )}
            <SeatSwapConfirmDialog
                opened={Boolean(pendingSwap)}
                source={selectedBooking}
                occupant={pendingSwap?.occupant ?? null}
                location={pendingSwap?.location ?? ''}
                registrations={registrations}
                confirming={Boolean(selectedBooking && isOperating(`booking-location-${selectedBooking.id}`))}
                onConfirm={handleConfirmSwap}
                onCancel={() => setPendingSwap(null)}
            />
        </Stack>
    );
};

export default LocationsSection;
